import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, ChevronRight } from 'lucide-react'
import { getDirectoryCounties, getDirectoryCountries } from '../../lib/api'
import type { DirectoryCounty } from '../../types'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { directoryCanonicalPath } from '../../lib/directoryCanonical'
import { NavCardGridSkeleton } from '../../components/ui/Skeletons'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'

function formatCountyName(name: string) {
  return name.replace(/,\s*(City|County)\s+of$/i, '')
}

const ENGLAND_REGION_ORDER = [
  'London', 'South East England', 'South West England', 'East of England',
  'East Midlands', 'West Midlands', 'Yorkshire and The Humber',
  'North West England', 'North East England',
]

function groupByRegion(counties: DirectoryCounty[]): Array<{ region: string; counties: DirectoryCounty[] }> {
  const map = new Map<string, DirectoryCounty[]>()
  for (const c of counties) {
    const r = c.adminRegion ?? 'Other'
    if (!map.has(r)) map.set(r, [])
    map.get(r)!.push(c)
  }
  const keys = [...map.keys()].sort((a, b) => {
    const ai = ENGLAND_REGION_ORDER.indexOf(a)
    const bi = ENGLAND_REGION_ORDER.indexOf(b)
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.localeCompare(b)
  })
  return keys.map(r => ({ region: r, counties: map.get(r)! }))
}

export function DirectoryCountry() {
  const { countrySlug } = useParams<{ countrySlug: string }>()
  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()
  const isEngland = countrySlug === 'england'

  const { data: counties, isLoading } = useQuery({
    queryKey: ['directory', 'counties', countrySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryCounties(countrySlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!countrySlug,
  })

  const { data: countries } = useQuery({
    queryKey: ['directory', 'countries', isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryCountries(isEmergency, isGasSafe, isWaterSafe),
  })

  const countryLabel = slugToLabel(countrySlug)
  const countryCount = countries?.find(c => c.countrySlug === countrySlug)?.count
  const totalPlumbers = countryCount ?? (counties?.reduce((s, c) => s + c.count, 0) ?? 0)
  const areaLabel = isEngland ? 'county'
    : countrySlug === 'scotland' ? 'council area'
    : countrySlug === 'wales' ? 'county'
    : 'district'

  const grouped = isEngland && counties ? groupByRegion(counties) : null

  return (
    <PageWrapper>
      <Seo
        title={`${label} in ${countryLabel} | FixDelivered`}
        description={`${totalPlumbers > 0 ? `${totalPlumbers} verified` : 'Find'} ${isEmergency ? 'emergency ' : isGasSafe ? 'Gas Safe ' : isWaterSafe ? 'WaterSafe ' : ''}plumbers in ${countryLabel}. Browse by ${areaLabel} to find a qualified local plumber.`}
        path={directoryCanonicalPath(countrySlug)}
        noindex={totalPlumbers === 0}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
        ]}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[{ label: countryLabel }]} />

            <div className="mt-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{label} in {countryLabel}</h1>
              <p className="text-gray-500 mt-1 text-sm min-h-[2.75rem] sm:min-h-[1.5rem]">
                {totalPlumbers > 0 ? `${totalPlumbers.toLocaleString()} verified plumbers. ` : ''}
                Select a {areaLabel} to browse local plumbers.
              </p>
              <DirectorySearchBar className="mt-3" />
            </div>

            {isLoading ? (
              <NavCardGridSkeleton count={8} />
            ) : isEngland && grouped ? (
              <div className="space-y-8">
                {grouped.map(({ region, counties: regionCounties }) => (
                  <div key={region}>
                    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">{region}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {regionCounties.map(c => (
                        <CountyCard key={c.countySlug} c={c} countrySlug={countrySlug!} basePath={basePath} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(counties ?? []).map(c => (
                  <CountyCard key={c.countySlug} c={c} countrySlug={countrySlug!} basePath={basePath} />
                ))}
              </div>
            )}

            {!isLoading && !counties?.length && (
              <div className="text-center py-16 text-gray-400">
                <p>No areas found.</p>
                <Link to={basePath} className="text-trust-500 hover:underline text-sm mt-2 inline-block">Back to all countries</Link>
              </div>
            )}
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar count={totalPlumbers} areaLabel={countryLabel} />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}

function CountyCard({ c, countrySlug, basePath }: { c: DirectoryCounty; countrySlug: string; basePath: string }) {
  return (
    <Link
      to={`${basePath}/${countrySlug}/${c.countySlug}`}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex items-center justify-between"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
          <Building2 className="h-4 w-4 text-gray-400" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 text-sm">{formatCountyName(c.county)}</p>
          <p className="text-xs text-gray-400 mt-0.5">{c.count} plumber{c.count !== 1 ? 's' : ''}</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors" />
    </Link>
  )
}
